import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Image,
  Text,
  Divider,
  CircularProgress,
  Box,
  Button,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
// Legacy Apple/Google-Pay PaymentRequest button — kept around but disabled
// while the new card-based auth+capture flow is in place. Restore by
// uncommenting the import + the <SimplePay /> usage below.
// import { SimplePay } from "../../Components/Buttons/simplePay";
import { CardPay } from "../../Components/Buttons/CardPay";
import { AccountArea } from "../../Components/Structure/accountArea";
import icon from "../../Assets/Images/icon.png";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { signOutIfProfileIncomplete } from "../../lib/socialAuth";
import { ConfirmFloating } from "./confirmFloating";

const AuthenticatedContent = ({
  event,
  formattedCost,
  formattedTax,
  formattedTotal,
  stripeTotal,
  user,
  step,
  setStep,
}) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAlreadyJoined, setIsAlreadyJoined] = useState(false);

  useEffect(() => {
    const fetchUserDataAndCheckMembership = async () => {
      if (user) {
        console.log(user);
        console.log(event);
        const db = getFirestore();
        const userRef = doc(db, "users", user.uid);

        const pollUserData = async () => {
          const docSnap = await getDoc(userRef);
          if (docSnap.exists() && docSnap.data().customerId) {
            const data = docSnap.data();
            setUserData(data);
            // Check if user is already in event.members
            if (
              Array.isArray(event.members) &&
              event.members.some((ref) => ref.id === user.uid)
            ) {
              setIsAlreadyJoined(true);
              setStep("success");
            }

            setLoading(false);
          } else {
            console.log("Polling for user data...");
            setTimeout(pollUserData, 1500);
          }
        };

        pollUserData();
      }
    };

    fetchUserDataAndCheckMembership();
  }, [user, event]);

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="350px"
      >
        <CircularProgress isIndeterminate color="blue.300" />
      </Box>
    );
  }

  if (step === "success" || isAlreadyJoined) {
    return <ConfirmFloating event={event} user={user} />;
  }

  return (
    <>
      <Flex flexDirection={"column"}>
        <Flex gap={4} mb={"10px"} flexDirection={["column", "column", "row"]}>
          <Image
            boxSize={"150px"}
            minW={["100%", "100%", "200px"]}
            borderRadius={"10px"}
            fit={"cover"}
            src={event.imageUrl}
            alt={event.name}
          />
          <Flex flexDirection={"column"} w={"100%"} justifyContent={"center"}>
            <Text fontWeight="extrabold" fontSize={"18px"} textAlign={"left"}>
              {event.name}
            </Text>
            <Text
              mt={"5px"}
              fontSize={"14px"}
              textAlign={"left"}
              fontWeight={"600"}
              color={"gray.600"}
            >
              Hosted by {event.memberData.displayName}
            </Text>
          </Flex>
        </Flex>

        <Divider />

        {/* Only show payment section if price > 0 */}
        {stripeTotal > 0 && (
          <>
            <Flex gap={2} mt={"10px"} mb={"10px"} flexDirection={"column"}>
              <Text fontWeight={"800"} fontSize={"12px"}>
                Payment Information
              </Text>
              <Flex justifyContent={"space-between"}>
                <Text fontWeight={"600"} fontSize={"16px"}>
                  1 Person
                </Text>
                <Text fontWeight={"600"} fontSize={"16px"}>
                  {formattedCost}
                </Text>
              </Flex>
              <Flex justifyContent={"space-between"}>
                <Text fontWeight={"600"} fontSize={"16px"}>
                  Tax
                </Text>
                <Text fontWeight={"600"} fontSize={"16px"}>
                  {formattedTax}
                </Text>
              </Flex>
              <Flex justifyContent={"space-between"}>
                <Text fontWeight={"900"} fontSize={"16px"}>
                  Total
                </Text>
                <Text fontWeight={"900"} fontSize={"16px"}>
                  {formattedTotal}
                </Text>
              </Flex>
            </Flex>
            <Divider mb={"10px"} />
          </>
        )}

        {/* Legacy single-step PaymentRequest button (Apple/Google Pay sheet)
            replaced by the new card auth+capture flow below.
        <SimplePay
          eventItem={event}
          price={stripeTotal}
          userData={userData}
          onSuccess={() => setStep("success")}
        /> */}

        <CardPay
          eventItem={event}
          price={stripeTotal}
          userData={userData}
          onSuccess={() => setStep("success")}
        />
      </Flex>
    </>
  );
};

export const PaymentFloting = ({ event, isOpen, onClose, user }) => {
  const taxAmount = event.cost * 0.07;
  const totalAmount = event.cost + taxAmount;
  const stripeTotal = Math.round(totalAmount * 100);
  const [step, setStep] = useState("payment");
  const [hasCompleteProfile, setHasCompleteProfile] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const profileCheckRef = useRef(0);

  // Check if user has complete profile in Firestore. A slow read from before
  // the profile was saved must not wipe a signup that just finished.
  useEffect(() => {
    const checkId = ++profileCheckRef.current;
    const checkProfile = async () => {
      if (user) {
        setCheckingProfile(true);
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (profileCheckRef.current !== checkId) return;
          setHasCompleteProfile(userDoc.exists() && !!userDoc.data().displayName);
        } catch (error) {
          console.error("Error checking profile:", error);
          if (profileCheckRef.current !== checkId) return;
          setHasCompleteProfile(false);
        }
        if (profileCheckRef.current === checkId) setCheckingProfile(false);
      } else {
        setHasCompleteProfile(false);
        setCheckingProfile(false);
      }
    };
    checkProfile();
  }, [user]);

  const handleSignupComplete = () => {
    profileCheckRef.current += 1;
    setCheckingProfile(false);
    setHasCompleteProfile(true);
  };

  const handleClose = async () => {
    await signOutIfProfileIncomplete();
    onClose();
  };

  const formattedCost = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(event.cost);

  const formattedTax = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(taxAmount);

  const formattedTotal = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(totalAmount);

  // Show authenticated content only if user has complete profile
  const showAuthContent = user && hasCompleteProfile && !checkingProfile;
  const isSuccessView = showAuthContent && step === "success";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={showAuthContent ? "xl" : "sm"}
      m={"20px"}
    >
      <ModalOverlay />
      <ModalContent
        bg={isSuccessView ? "transparent" : "white"}
        boxShadow={isSuccessView ? "none" : undefined}
        borderRadius={isSuccessView ? "24px" : undefined}
      >
        {!isSuccessView && (
          <ModalHeader
            p={showAuthContent ? 3 : 6}
            pb={0}
            className="font-gilroy-bold"
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            {showAuthContent ? (
              "Purchase Ticket"
            ) : (
              <>
                <Image
                  borderRadius={"10px"}
                  src={icon}
                  alt="Slideshow Image"
                  h={"35px"}
                  objectFit="contain"
                />
                <Text mt={4}>Welcome to Proximy</Text>
              </>
            )}
          </ModalHeader>
        )}
        <ModalCloseButton
          color={isSuccessView ? "gray.800" : undefined}
          bg={isSuccessView ? "white" : undefined}
          borderRadius={isSuccessView ? "full" : undefined}
          boxShadow={isSuccessView ? "md" : undefined}
          zIndex={isSuccessView ? 20 : undefined}
          top={isSuccessView ? 3 : undefined}
          right={isSuccessView ? 3 : undefined}
          _hover={isSuccessView ? { bg: "gray.100" } : undefined}
        />
        <ModalBody p={isSuccessView ? 0 : undefined} px={isSuccessView ? 0 : 3}>
          {showAuthContent ? (
            <AuthenticatedContent
              event={event}
              user={user}
              formattedCost={formattedCost}
              formattedTax={formattedTax}
              formattedTotal={formattedTotal}
              stripeTotal={stripeTotal}
              step={step}
              setStep={setStep}
            />
          ) : (
            <AccountArea isOpen={isOpen} onSignupComplete={handleSignupComplete} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
